import { INT_TO_WORD } from "@/components/pieces/coaching/constants";
import { getRoundState } from "@/components/pieces/coaching/reviews/utils";
import { FullReview } from "@/types/meetings";
import { formatDate } from "@/utils/datetime";
import { NonNullableArrayItem } from "@/utils/types";

import { ReviewTimelineItem } from "./ReviewTimelineItem";

type ReviewRoundType = NonNullableArrayItem<FullReview["rounds"]>;

function reviewCompletedAt(completedAt?: Date) {
  return completedAt
    ? `Sent on ${formatDate(completedAt, "MMM d 'at' h:mm a z")}`
    : "Review sent";
}

const REVIEW_ROUND_STATES = {
  CUSTOMER: {
    PENDING: {
      intent: "active",
      qualifier: "(Waiting For Coach)",
      message: "We have notified your coach about this review",
    },
    CANCELED: {
      intent: "canceled",
      qualifier: "",
      message: "",
    },
    IN_PROGRESS: {
      intent: "active",
      qualifier: "In Progress",
      message: "Your coach will send you feedback as soon as they are finished",
    },
    AWAITING_REVISION: {
      intent: "success",
      qualifier: "Complete",
      message: (completedAt?: Date) =>
        `${reviewCompletedAt(
          completedAt,
        )}. Make the suggested changes and send it back in the chat.`,
    },
    COMPLETE: {
      intent: "success",
      qualifier: "Complete",
      message: reviewCompletedAt,
    },
  },
  COACH: {
    PENDING: {
      intent: "active",
      qualifier: "(Waiting For Coach)",
      message: "Please accept or decline this review ASAP",
    },
    CANCELED: {
      intent: "canceled",
      qualifier: "",
      message: "",
    },
    IN_PROGRESS: {
      intent: "active",
      qualifier: "In Progress",
      message: "Please complete the request",
    },
    AWAITING_REVISION: {
      intent: "success",
      qualifier: "Complete",
      message: (completedAt?: Date) =>
        `${reviewCompletedAt(
          completedAt,
        )}. When the customer sends their revisions, please take the next step.`,
    },
    COMPLETE: {
      intent: "success",
      qualifier: "Complete",
      message: reviewCompletedAt,
    },
  },
};

interface ReviewRoundProps {
  review: FullReview;
  reviewRound: ReviewRoundType;
  roundNum: number;
  rounds: ReviewRoundType[];
  isCoach: boolean;
}

export function ReviewRound({
  review,
  reviewRound,
  roundNum,
  rounds,
  isCoach,
}: ReviewRoundProps) {
  const { numRounds } = review;
  const actor = isCoach ? "COACH" : "CUSTOMER";
  const { intent, qualifier, message } =
    REVIEW_ROUND_STATES[actor][getRoundState(review, rounds, roundNum)];
  const roundLabel =
    numRounds === 1 ? "Review" : `${INT_TO_WORD[roundNum]} Review`;

  return (
    <ReviewTimelineItem
      intent={intent}
      label={`${roundLabel} ${qualifier}`}
      message={
        typeof message === "function"
          ? message(
              reviewRound.completedAt
                ? new Date(reviewRound.completedAt)
                : undefined,
            )
          : message
      }
    />
  );
}
